/**
 * FP3-030 — Accept Invite page.
 *
 * Public page (no auth required). Reads `?token=<raw>` from the URL,
 * lets the invitee set a password, calls /api/invites/accept, and on
 * success the backend logs the user in via cookies (the response sets
 * fp_access/fp_refresh). We then navigate to /.
 *
 * Token never leaves the URL bar / form submit; we never echo it,
 * never store it in localStorage, never log it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This invite link is missing a token.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      // FP3-042C — Hard 30s cap so the page never appears to hang
      // indefinitely when an upstream proxy times out. The backend's
      // happy path completes in well under a second; anything close
      // to 30s is a real network/edge problem.
      await api.acceptInvite({ token, password }, { timeout: 30000 });
      // Backend already set the session cookie; rehydrate auth context.
      // Failure here is non-fatal — the account is activated and the
      // cookies are set; we can just send the user to /login as a
      // safety net.
      try {
        if (refresh) await refresh();
        toast.success("Welcome to Frame Practice");
        navigate("/", { replace: true });
      } catch {
        toast.success("Account activated — please sign in");
        navigate("/login", { replace: true });
      }
    } catch (err) {
      // FP3-042C — Branch on error shape so the user sees an
      // actionable message instead of the historical generic
      // fallback. This is the field where production users were
      // getting stuck.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = err?.code;
      if (status === 409 && /already/i.test(String(detail || ""))) {
        // Invite was already accepted — send the user to /login
        // instead of leaving them stranded on the activation page.
        toast.info(detail || "Account already activated");
        navigate("/login", { replace: true });
        return;
      }
      if (code === "ECONNABORTED") {
        setError(
          "Activation is taking longer than expected. Please check your connection and try again, or contact your administrator if the problem persists."
        );
      } else if (!err?.response) {
        setError(
          "We could not reach the server. Please check your connection and try again."
        );
      } else if (status >= 500) {
        setError(
          "Server error while activating your account. Please try again, or ask your administrator to resend the invite."
        );
      } else {
        setError(detail || "This invite link is invalid or has expired.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="accept-invite-page"
      className="min-h-screen bg-[#F7F5EF] body-font flex items-center justify-center px-5 py-10"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-bold heading-font">F</span>
          </div>
          <div className="leading-tight">
            <div className="text-[17px] font-bold text-[#1E3F33] heading-font tracking-tight">
              Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
              Provider workspace
            </div>
          </div>
        </div>

        <Card
          data-testid="accept-invite-card"
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-7 sm:p-8">
            <div
              data-testid="accept-invite-pill"
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
            >
              <ShieldCheck size={11} weight="duotone" />
              Invite-only access
            </div>
            <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
              Set your password
            </h1>
            <p className="text-[12.5px] text-[#6B7280] mt-1.5">
              Finish setting up your Frame Practice account.
            </p>

            <form
              data-testid="accept-invite-form"
              onSubmit={onSubmit}
              className="space-y-4 mt-6"
              noValidate
            >
              <div>
                <Label
                  htmlFor="ai-password"
                  className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                >
                  New password
                </Label>
                <PasswordInput
                  id="ai-password"
                  data-testid="accept-invite-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </div>
              <div>
                <Label
                  htmlFor="ai-confirm"
                  className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                >
                  Confirm password
                </Label>
                <PasswordInput
                  id="ai-confirm"
                  data-testid="accept-invite-confirm"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </div>

              {error && (
                <div
                  data-testid="accept-invite-error"
                  role="alert"
                  className="text-[12.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2 leading-relaxed"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="accept-invite-submit"
                disabled={submitting || !token}
                className="w-full h-10 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
              >
                {submitting ? "Activating…" : "Accept invite"}
              </Button>
            </form>

            <p className="mt-5 text-[11.5px] text-[#6B7280]">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
              >
                Sign in →
              </Link>
            </p>
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-[#9AA0A6] mt-5">
          © Frame Practice · invite-only provider workspace
        </p>
      </div>
    </div>
  );
}
